import { useEffect } from "react";
import type { ReactNode } from "react";

export function Screen({ title, sub, children, action }: {
  title: string; sub?: string | undefined; children: ReactNode; action?: ReactNode | undefined;
}) {
  return (
    <div className="mx-auto w-full max-w-[860px] px-4 py-6 split:px-6">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="font-num text-score font-semibold">{title}</h1>
          {sub && <p className="mt-1 text-body text-faint">{sub}</p>}
        </div>
        {action}
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

export function Card({ children }: { children: ReactNode }) {
  return <div className="overflow-hidden rounded-xl border border-line bg-panel">{children}</div>;
}

export function Row({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  const cls = "flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0";
  return onClick
    ? <button onClick={onClick} className={`${cls} w-full text-left hover:bg-raise`}>{children}</button>
    : <div className={cls}>{children}</div>;
}

export function Button({ children, onClick, kind = "primary", disabled, full }: {
  children: ReactNode; onClick?: () => void;
  kind?: "primary" | "ghost" | "danger"; disabled?: boolean; full?: boolean;
}) {
  const base = "rounded-card px-4 py-3 text-body font-semibold transition-colors disabled:opacity-40";
  const look =
    kind === "primary" ? "bg-amber text-board hover:brightness-110"
    : kind === "danger" ? "border border-[#3A2422] text-leather hover:bg-[#1d1412]"
    : "border border-line text-chalk hover:bg-raise";
  return (
    <button onClick={onClick} disabled={disabled}
      className={`${base} ${look} ${full ? "w-full" : ""}`}>
      {children}
    </button>
  );
}

export function Field({ label, ...props }: {
  label: string;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-mono text-label uppercase tracking-[.14em] text-faint">
        {label}
      </span>
      <input
        {...props}
        className="w-full rounded-card border border-line bg-[#0F1317] px-3 py-3 text-lead
                   text-chalk outline-none placeholder:text-faint focus:border-amber"
      />
    </label>
  );
}

/**
 * Nothing here yet.
 *
 * Takes an action, because used 29 times an empty state that only says "no
 * teams" wastes the one moment the person is definitely looking for the button
 * that would add one.
 */
export function Empty({ title, children, action }: {
  title: string; children?: ReactNode;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="px-6 py-12 text-center text-body text-faint">
      <b className="mb-1.5 block font-num text-title text-chalk">{title}</b>
      {children}
      {action && (
        <button onClick={action.onClick}
          className="mx-auto mt-4 block min-h-[44px] rounded-control border border-amber
                     px-5 text-body text-amber">
          {action.label}
        </button>
      )}
    </div>
  );
}

export function Msg({ text, kind }: { text: string | null; kind: "err" | "ok" | "warn" }) {
  if (!text) return null;
  const look =
    kind === "err" ? "border-[#3A2422] bg-[#2A1614] text-leather"
    : kind === "ok" ? "border-[#1E3830] bg-[#14251F] text-crease"
    : "border-[#4A3A1C] bg-[#241C0C] text-amber";
  return (
    <div className={`mb-4 rounded-control border px-3.5 py-2.5 text-body ${look}`}>{text}</div>
  );
}

/* ── the patterns that kept getting rewritten ───────────────────────────── */

/**
 * A section heading.
 *
 * This exact style — 10px mono, uppercase, wide tracking, faint — was written
 * out by hand 35 times across 7 files. Uppercase has no descenders and mono is
 * wide, so cap height and letter-spacing carry it at a size that would be
 * unreadable as lowercase prose.
 */
export function SectionLabel({ children, className = "" }: {
  children: ReactNode; className?: string;
}) {
  return (
    <span className={`font-mono text-label uppercase tracking-[.14em] text-faint ${className}`}>
      {children}
    </span>
  );
}

/**
 * A switch.
 *
 * Promoted from RulesForm, which had the best version. min-h-44 on the whole
 * row rather than the track: a toggle with no explanatory note collapsed to
 * 28px, so how easy it was to hit depended on how much prose sat under it.
 */
export function Toggle({ on, onChange, label, note, disabled }: {
  on: boolean; onChange: (v: boolean) => void;
  label: string; note?: string; disabled?: boolean;
}) {
  return (
    <button onClick={() => !disabled && onChange(!on)} disabled={disabled}
      role="switch" aria-checked={on} aria-label={label}
      className="flex min-h-[44px] w-full items-start gap-3 py-1 text-left disabled:opacity-40">
      <span className={`mt-[2px] flex h-[26px] w-[44px] shrink-0 items-center rounded-full
                        border px-[3px] ${on ? "border-amber bg-amber" : "border-line bg-raise"}`}>
        <span className={`h-[18px] w-[18px] rounded-full bg-chalk transition-transform
                          ${on ? "translate-x-[18px]" : ""}`} />
      </span>
      <span className="min-w-0 flex-1">
        <b className={`block text-body font-semibold ${on ? "text-amber" : ""}`}>{label}</b>
        {note && <span className="mt-0.5 block text-meta leading-relaxed text-faint">{note}</span>}
      </span>
    </button>
  );
}

/** A selectable pill. Six inline versions disagreed about what "selected" looks like. */
export function Chip({ on, onClick, children, disabled, className = "" }: {
  on: boolean; onClick: () => void; children: ReactNode;
  disabled?: boolean; className?: string;
}) {
  return (
    <button onClick={onClick} disabled={disabled} aria-pressed={on}
      className={`min-h-[44px] rounded-control border px-3.5 text-caption disabled:opacity-40
        ${on ? "border-amber bg-[#20180C] text-amber" : "border-line bg-raise"} ${className}`}>
      {children}
    </button>
  );
}

/**
 * Confirm before something irreversible.
 *
 * Three screens each grew their own version of this, and they disagreed about
 * button order and wording. Destructive UX is the last place that should vary:
 * the muted option is always the one that changes nothing, and it is always on
 * the left.
 */
export function Confirm({ question, confirmLabel, onConfirm, onCancel, cancelLabel = "Cancel" }: {
  question: ReactNode; confirmLabel: string;
  onConfirm: () => void; onCancel: () => void; cancelLabel?: string;
}) {
  return (
    <div className="rounded-control border border-line bg-raise p-3">
      <div className="mb-2.5 text-caption leading-relaxed">{question}</div>
      <div className="flex gap-2">
        <button onClick={onCancel}
          className="min-h-[44px] flex-1 rounded-control border border-line text-body">
          {cancelLabel}
        </button>
        <button onClick={onConfirm}
          className="min-h-[44px] flex-1 rounded-control border border-[#3A2422] text-body text-leather">
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}

/** An expandable row. Three screens rolled their own, with different a11y. */
export function Disclosure({ summary, children, open, onToggle }: {
  summary: ReactNode; children: ReactNode; open: boolean; onToggle: () => void;
}) {
  return (
    <div className="border-b border-line last:border-b-0">
      <button onClick={onToggle} aria-expanded={open}
        className="flex min-h-[44px] w-full items-center gap-3 px-4 py-3 text-left">
        {summary}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

/**
 * A number that has to line up.
 *
 * tabular-nums so a column of scores does not jitter as the digits change —
 * proportional figures make 111 narrower than 000, which is visible in a
 * scorecard and maddening in a live total.
 */
export function Stat({ value, label, className = "" }: {
  value: ReactNode; label?: ReactNode; className?: string;
}) {
  return (
    <span className={`tabular-nums ${className}`}>
      {value}
      {label && <span className="ml-1.5 text-meta text-faint">{label}</span>}
    </span>
  );
}

/**
 * A status badge.
 *
 * Status was rendered as `{status.toLowerCase()}` in five places — "live",
 * "complete", "draft" as plain grey text, indistinguishable from a caption. A
 * match being live is the single most scannable fact in a list and it looked
 * like nothing.
 *
 * Colour carries meaning here, so the word is always present too: colour alone
 * fails for the roughly one man in twelve with a colour vision deficiency, and
 * this is a cricket club.
 */
const BADGE: Record<string, string> = {
  LIVE:      "border-leather text-leather",
  COMPLETE:  "border-crease text-crease",
  SCHEDULED: "border-line text-faint",
  DRAFT:     "border-line text-faint",
  PUBLISHED: "border-amber text-amber",
  ABANDONED: "border-line text-faint",
};

export function Badge({ status, pulse }: { status: string; pulse?: boolean }) {
  const look = BADGE[status.toUpperCase()] ?? "border-line text-faint";
  return (
    <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1
                      font-mono text-label uppercase tracking-[.1em] ${look}`}>
      {pulse && <i className="h-[6px] w-[6px] rounded-full bg-current motion-safe:animate-pulse" />}
      {status.toLowerCase()}
    </span>
  );
}

/**
 * A bottom sheet.
 *
 * Three screens had grown their own fixed-inset overlay. On a phone held in one
 * hand a sheet is reachable and a centred dialog is not, so this always rises
 * from the bottom.
 *
 * Dismissing by tapping the backdrop is deliberate and so is the Escape key —
 * a sheet you cannot get out of is a trap, and a scorer mid-over has no
 * patience for hunting a close button.
 */
export function Sheet({ open, onClose, title, children }: {
  open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end motion-safe:animate-fade"
         role="dialog" aria-modal="true">
      <button aria-label="Close" onClick={onClose}
              className="absolute inset-0 bg-black/60" />
      <div className="relative max-h-[85vh] w-full overflow-y-auto rounded-t-card border-t
                      border-line bg-panel pb-[calc(16px+var(--sab))] motion-safe:animate-sheet">
        {/* A grab handle, because a sheet should look like it can be dismissed. */}
        <div className="sticky top-0 bg-panel pt-2">
          <div className="mx-auto h-1 w-9 rounded-full bg-line" />
          {title && (
            <div className="flex items-center px-4 pb-2 pt-3">
              <b className="text-lead font-semibold">{title}</b>
              <button onClick={onClose} aria-label="Close"
                className="ml-auto min-h-[44px] px-2 font-mono text-label uppercase
                           tracking-[.12em] text-faint">
                Done
              </button>
            </div>
          )}
        </div>
        <div className="px-4">{children}</div>
      </div>
    </div>
  );
}

/**
 * A message that does not move the page.
 *
 * Confirmations were written into a banner that pushed everything below it
 * down — on the scorer screen that means the keypad jumps under your thumb at
 * the moment you are most likely to tap it again.
 */
export function Toast({ text, onDone }: { text: string | null; onDone: () => void }) {
  useEffect(() => {
    if (!text) return;
    const t = setTimeout(onDone, 2600);
    return () => clearTimeout(t);
  }, [text, onDone]);

  if (!text) return null;
  return (
    <div role="status" aria-live="polite"
         className="pointer-events-none fixed inset-x-0 bottom-[calc(72px+var(--sab))] z-40
                    flex justify-center px-4 motion-safe:animate-rise">
      <span className="rounded-card border border-line bg-panel px-4 py-2.5 text-body shadow-lg">
        {text}
      </span>
    </div>
  );
}

/** A placeholder row, so a loading list does not flash an empty layout. */
export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-4" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-11 rounded-control
                                bg-[linear-gradient(90deg,#14181C,#1A2027,#14181C)]
                                bg-[length:200%_100%] motion-safe:animate-shimmer" />
      ))}
    </div>
  );
}
